import { useMutation, useQuery } from '@tanstack/react-query'
import { api, type PublicPlan } from '../../lib/api'
import { ACTIVE_ORGANIZATION_KEY, SELECTED_PLAN_PRICE_KEY } from '../../lib/brandStorage'

export function BillingPage() {
  const organizationId = localStorage.getItem(ACTIVE_ORGANIZATION_KEY) ?? ''
  const locallySelectedPriceId = localStorage.getItem(SELECTED_PLAN_PRICE_KEY) ?? ''
  const plans = useQuery({ queryKey: ['public-plans'], queryFn: api.publicPlans })
  const configuration = useQuery({ queryKey: ['billing-configuration'], queryFn: api.billingConfiguration })
  const onboarding = useQuery({ queryKey: ['onboarding'], queryFn: api.onboardingStatus })
  const subscription = useQuery({
    queryKey: ['subscription', organizationId],
    queryFn: () => api.subscription(organizationId),
    enabled: Boolean(organizationId),
  })
  const checkout = useMutation({
    mutationFn: async (priceId: string) => {
      if (!organizationId) throw new Error('Selecione uma empresa antes de contratar um plano.')
      localStorage.setItem(SELECTED_PLAN_PRICE_KEY, priceId)
      const session = await api.createBillingCheckout(organizationId, priceId, crypto.randomUUID())
      if (!session.checkoutUrl) throw new Error('O Mercado Pago não devolveu uma URL de checkout.')
      return session.checkoutUrl
    },
    onSuccess: checkoutUrl => window.location.assign(checkoutUrl),
  })
  const displayPlans = (plans.data?.items ?? []).flatMap(plan => {
    const price = plan.prices.find(item => item.billingInterval === 'month' && item.currency === 'BRL')
    return price ? [{ plan, price }] : []
  })
  const selectedPriceId = locallySelectedPriceId || onboarding.data?.selectedPlanPriceId || ''

  return (
    <main className="billing-page">
      <header className="page-heading">
        <div><p className="eyebrow">ASSINATURA</p><h1>Escolha o plano da sua operação</h1></div>
        {subscription.data && <p className="subscription-summary">Plano atual: <strong>{subscription.data.plan.name}</strong><span>{subscription.data.status === 'trialing' ? 'Período de avaliação' : subscription.data.status}</span></p>}
      </header>

      {!organizationId && <p className="error">Selecione uma empresa na visão geral antes de contratar.</p>}
      {configuration.data && !configuration.data.checkoutConfigured && <p className="error">O checkout ainda não está configurado neste ambiente.</p>}
      {configuration.data?.checkoutConfigured && !configuration.data.webhookConfigured && <p className="billing-warning">O checkout está ativo, mas o webhook ainda precisa ser configurado para confirmar pagamentos automaticamente.</p>}
      {(plans.isError || configuration.isError) && <p className="error">Não foi possível carregar a configuração de cobrança.</p>}

      <section className="billing-plan-grid" aria-label="Planos disponíveis">
        {plans.isPending && <p>Carregando planos…</p>}
        {displayPlans.map(({ plan, price }) => (
          <article className={price.id === selectedPriceId ? 'billing-plan selected' : 'billing-plan'} key={plan.id}>
            {price.id === selectedPriceId && <span className="selected-label">Sua escolha</span>}
            <h2>{plan.name}</h2>
            <p>{plan.description}</p>
            <div className="billing-price"><strong>{formatMoney(price.amountCents)}</strong><span>/mês</span></div>
            <ul>{planFeatures(plan).map(feature => <li key={feature}>✓ {feature}</li>)}</ul>
            <button
              type="button"
              disabled={!organizationId || !configuration.data?.checkoutConfigured || checkout.isPending}
              onClick={() => checkout.mutate(price.id)}
            >
              {checkout.isPending && checkout.variables === price.id ? 'Abrindo checkout…' : `Assinar ${plan.name}`}
            </button>
          </article>
        ))}
      </section>
      {checkout.isError && <p className="error">{checkout.error.message}</p>}
      <p className="billing-footnote">A contratação é concluída no ambiente seguro do Mercado Pago.</p>
    </main>
  )
}

function formatMoney(amountCents: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amountCents / 100)
}

function planFeatures(plan: PublicPlan) {
  const enabled = (plan.capabilities ?? []).filter(item => item.enabled)
  const members = enabled.find(item => item.key === 'members.max')
  const features = [members?.limit === 1 ? '1 usuário' : members?.limit ? `Até ${members.limit} usuários` : 'Usuários ilimitados']
  features.push('Acesso a todas as funcionalidades')
  return features
}
